/**
 * @rogueworlds/client — `MeshData` (from the mesher worker) -> one `THREE.Mesh` per chunk.
 *
 * One material is shared by every chunk, so a chunk-count increase only ever changes draw calls,
 * never the number of shader compiles (convention 6). The material samples the terrain atlas per
 * VOXEL while greedy quads span many voxels, which needs a small amount of shader surgery:
 * `chunk-renderer` injects a `map_fragment` replacement that maps `fract(uv)` (uv is in voxel units,
 * per the voxel-bridge output contract) into the quad's atlas cell. The cell origin travels per
 * vertex as the second geometry attribute, converted from `MeshData.tiles` on the CPU.
 *
 * If a future three.js release renames the shader chunk entry points, the injection probe
 * (`terrainMaterialPath`) fails closed and the renderer ships the vertex-colour material instead —
 * `MeshData.colors` carries the mesher's face shading, and the block tint is folded in on the CPU,
 * so a fallback chunk is still coloured and lit. It is never an invisible mesh.
 *
 * Mesh space: sticky details from the voxel-bridge contract — positions are chunk-local in VOXEL
 * units (0..32, a face on plane 32 is legal), so the scale to metres is applied here and the mesh is
 * translated to `voxelOfChunk(cx, cy, cz) * VOXEL_SIZE_M`. Chunk meshes RECEIVE shadows and do not
 * cast them: with the sun's frustum following the player, letting 600 chunks cast would double the
 * shadow pass's draw calls for no visual gain on flat voxel terrain (convention 8).
 */

import * as THREE from "three";
import { VOXEL_SIZE_M, decodeChunkKey } from "@rogueworlds/shared";
import type { ChunkKey } from "@rogueworlds/shared";
import { BLOCKS, voxelOfChunk } from "@rogueworlds/voxel-bridge";
import type { MeshData } from "@rogueworlds/voxel-bridge";

import { ATLAS_COLUMNS, ATLAS_ROWS, ATLAS_TILE_PX, atlasCell } from "./atlas";

/** Geometry attribute carrying the atlas cell origin, per vertex. */
const TILE_ATTRIBUTE = "tileCell";

const ATLAS_TILES = ATLAS_COLUMNS * ATLAS_ROWS;

/**
 * Tile index -> cell origin, resolved once at module load through the atlas' own `atlasCell`, so the
 * layout (and the `flipY` compensation) lives in exactly one place and the geometry build loop stays
 * allocation-free. Index 0 is the atlas' "missing tile" cell.
 */
const CELL_UV = new Float32Array(ATLAS_TILES * 2);
for (let tile = 0; tile < ATLAS_TILES; tile++) {
  const cell = atlasCell(tile);
  CELL_UV[tile * 2] = cell.u;
  CELL_UV[tile * 2 + 1] = cell.v;
}

/** three.js shader chunk entry points the atlas material patches. */
const VERTEX_PARS_TARGET = "#include <uv_pars_vertex>";
const VERTEX_ASSIGN_TARGET = "#include <uv_vertex>";
const FRAGMENT_PARS_TARGET = "#include <uv_pars_fragment>";
const FRAGMENT_MAP_TARGET = "#include <map_fragment>";

/**
 * The injected `map_fragment`. `fract(vMapUv)` repeats the atlas cell once per 10 cm voxel across a
 * merged quad, and the sample is pulled to the centre of the outermost texel of the cell
 * (`(0..15 voxels) * 15 + 0.5) / 16`) so nearest filtering can never read a neighbouring cell.
 */
function atlasMapFragment(): string {
  const insideCellSpan = (ATLAS_TILE_PX - 1).toFixed(1);
  const tileSize = ATLAS_TILE_PX.toFixed(1);
  const cellStepU = (1 / ATLAS_COLUMNS).toFixed(4);
  const cellStepV = (1 / ATLAS_ROWS).toFixed(4);
  return `
#ifdef USE_MAP
	vec2 rwCellLocal = ( fract( vMapUv ) * ${insideCellSpan} + 0.5 ) / ${tileSize};
	diffuseColor *= texture2D( map, vTileCell + rwCellLocal * vec2( ${cellStepU}, ${cellStepV} ) );
#endif
`;
}

/**
 * Fails closed: confirms that three's Lambert shader still contains the four include points the
 * injection depends on. Verified against three 0.186 (`meshlambert_vert`/`meshlambert_frag`).
 */
function shaderInjectionSupported(): boolean {
  const lib = THREE.ShaderLib as unknown as Record<
    string,
    { vertexShader: string; fragmentShader: string } | undefined
  >;
  const lambert = lib["meshlambert"] ?? lib["lambert"];
  if (lambert === undefined) {
    return false;
  }
  return (
    lambert.vertexShader.includes(VERTEX_PARS_TARGET) &&
    lambert.vertexShader.includes(VERTEX_ASSIGN_TARGET) &&
    lambert.fragmentShader.includes(FRAGMENT_PARS_TARGET) &&
    lambert.fragmentShader.includes(FRAGMENT_MAP_TARGET)
  );
}

const ATLAS_SHADER_READY = shaderInjectionSupported();

/** Which material path chunk meshes use. Additive export, for diagnostics and the build report. */
export function terrainMaterialPath(): "atlas" | "vertex-color" {
  return ATLAS_SHADER_READY ? "atlas" : "vertex-color";
}

if (!ATLAS_SHADER_READY) {
  console.warn(
    "[rw] three.js Lambert shader chunks changed: chunk meshes render with vertex colours (atlas disabled).",
  );
}

function createTerrainMaterial(atlas: THREE.Texture): THREE.MeshLambertMaterial {
  const material = new THREE.MeshLambertMaterial({ map: atlas });
  material.onBeforeCompile = (shader): void => {
    shader.vertexShader = shader.vertexShader
      .replace(VERTEX_PARS_TARGET, `${VERTEX_PARS_TARGET}\nattribute vec2 ${TILE_ATTRIBUTE};\nvarying vec2 vTileCell;`)
      .replace(VERTEX_ASSIGN_TARGET, `${VERTEX_ASSIGN_TARGET}\nvTileCell = ${TILE_ATTRIBUTE};`);
    shader.fragmentShader = shader.fragmentShader
      .replace(FRAGMENT_PARS_TARGET, `${FRAGMENT_PARS_TARGET}\nvarying vec2 vTileCell;`)
      .replace(FRAGMENT_MAP_TARGET, atlasMapFragment());
  };
  // Without this key a plain Lambert material with equal parameters could reuse our program (or we
  // could reuse its), and one of the two would render with the wrong shader.
  material.customProgramCacheKey = (): string => "rw-terrain-atlas-v1";
  return material;
}

/** A `RangeError` here means the mesher and the renderer disagree: loud beats a silently empty mesh. */
function assertLength(length: number, expected: number, name: string): void {
  if (length !== expected) {
    throw new RangeError(`ChunkRenderer: MeshData.${name} has ${length} values, expected ${expected}`);
  }
}

/**
 * Fallback path only: multiplies the mesher's face shading by the block's registry tint, in place,
 * so a chunk without the atlas material is still recognisably stone/grass/sand.
 */
function paintBlockColors(colors: Float32Array, tiles: Float32Array, vertexCount: number, components: number): void {
  for (let vertex = 0; vertex < vertexCount; vertex++) {
    const raw = Math.floor(tiles[vertex] ?? 0);
    const def = BLOCKS[raw < 0 ? 0 : raw];
    const red = def === undefined ? 1 : def.color[0];
    const green = def === undefined ? 1 : def.color[1];
    const blue = def === undefined ? 1 : def.color[2];
    const at = vertex * components;
    colors[at] = (colors[at] ?? 1) * red;
    colors[at + 1] = (colors[at + 1] ?? 1) * green;
    colors[at + 2] = (colors[at + 2] ?? 1) * blue;
  }
}

interface ChunkEntry {
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshLambertMaterial>;
  geometry: THREE.BufferGeometry;
  triangles: number;
}

export class ChunkRenderer {
  /** Which material this renderer shipped with: `"atlas"` normally, `"vertex-color"` if degraded. */
  readonly materialPath: "atlas" | "vertex-color";

  private readonly scene: THREE.Scene;
  private readonly material: THREE.MeshLambertMaterial;
  private readonly chunks = new Map<ChunkKey, ChunkEntry>();
  private triangles = 0;

  constructor(scene: THREE.Scene, atlas: THREE.Texture) {
    this.scene = scene;
    this.materialPath = terrainMaterialPath();
    this.material = this.materialPath === "atlas" ? createTerrainMaterial(atlas) : new THREE.MeshLambertMaterial({ vertexColors: true });
  }

  /**
   * Creates or replaces the mesh for `key`. Takes ownership of the `MeshData` arrays: positions are
   * scaled from voxel units to metres in place, which is safe because a transferred ArrayBuffer has
   * exactly one owner. Replacing disposes the previous geometry and reuses the material.
   */
  upsert(key: ChunkKey, mesh: MeshData): void {
    const geometry = this.buildGeometry(mesh);
    if (geometry === null) {
      // An all-air chunk (or a fully hidden one) has no geometry at all: keep the map honest.
      this.remove(key);
      return;
    }
    const index = geometry.index;
    const triangles = index === null ? 0 : Math.floor(index.count / 3);

    const existing = this.chunks.get(key);
    if (existing !== undefined) {
      existing.mesh.geometry = geometry;
      existing.geometry.dispose();
      existing.geometry = geometry;
      this.triangles += triangles - existing.triangles;
      existing.triangles = triangles;
      return;
    }

    const { cx, cy, cz } = decodeChunkKey(key);
    const origin = voxelOfChunk(cx, cy, cz);
    const node = new THREE.Mesh(geometry, this.material);
    node.position.set(origin.bx * VOXEL_SIZE_M, origin.by * VOXEL_SIZE_M, origin.bz * VOXEL_SIZE_M);
    // Static transform: skip the per-frame matrix recompose for hundreds of chunk meshes.
    node.matrixAutoUpdate = false;
    node.updateMatrix();
    node.castShadow = false;
    node.receiveShadow = true;
    this.scene.add(node);

    this.chunks.set(key, { mesh: node, geometry, triangles });
    this.triangles += triangles;
  }

  /** Removes and frees the chunk's geometry. Materials and the atlas are shared and stay alive. */
  remove(key: ChunkKey): void {
    const entry = this.chunks.get(key);
    if (entry === undefined) {
      return;
    }
    this.scene.remove(entry.mesh);
    entry.geometry.dispose();
    this.triangles -= entry.triangles;
    this.chunks.delete(key);
  }

  has(key: ChunkKey): boolean {
    return this.chunks.has(key);
  }

  /** Removes every chunk. The shared material survives, so the renderer stays usable. */
  clear(): void {
    for (const key of Array.from(this.chunks.keys())) {
      this.remove(key);
    }
    this.triangles = 0;
  }

  get meshCount(): number {
    return this.chunks.size;
  }

  get triangleCount(): number {
    return this.triangles;
  }

  /** Frees every chunk geometry and the shared material. The atlas texture belongs to the caller. */
  dispose(): void {
    this.clear();
    this.material.dispose();
  }

  private buildGeometry(data: MeshData): THREE.BufferGeometry | null {
    const positions = data.positions;
    const vertexCount = Math.floor(positions.length / 3);
    const indexCount = data.indices.length;
    if (vertexCount === 0 || indexCount === 0) {
      return null;
    }
    assertLength(positions.length, vertexCount * 3, "positions");
    assertLength(data.normals.length, vertexCount * 3, "normals");
    assertLength(data.uvs.length, vertexCount * 2, "uvs");
    assertLength(data.tiles.length, vertexCount, "tiles");
    if (indexCount % 3 !== 0) {
      throw new RangeError(`ChunkRenderer: MeshData.indices has ${indexCount} values, expected a multiple of 3`);
    }
    const colorComponents = data.colors.length === vertexCount * 3 ? 3 : data.colors.length === vertexCount * 4 ? 4 : 0;
    if (colorComponents === 0) {
      throw new RangeError(
        `ChunkRenderer: MeshData.colors has ${data.colors.length} values for ${vertexCount} vertices`,
      );
    }

    // Voxel units -> metres, in place (see the ownership note on `upsert`).
    for (let i = 0; i < positions.length; i++) {
      positions[i] = (positions[i] ?? 0) * VOXEL_SIZE_M;
    }
    if (this.materialPath === "vertex-color") {
      paintBlockColors(data.colors, data.tiles, vertexCount, colorComponents);
    }

    // Tile index -> cell origin. Normalised bytes keep this attribute at 2 bytes per vertex; the
    // quantisation error is at most 1/255 of a cell, i.e. 0.016 of a texel, and the shader's own
    // half-texel inset (0.5 of a texel) absorbs it with three orders of magnitude to spare.
    const tileCells = new Uint8Array(vertexCount * 2);
    for (let vertex = 0; vertex < vertexCount; vertex++) {
      const raw = Math.floor(data.tiles[vertex] ?? 0);
      const tile = raw >= 0 && raw < ATLAS_TILES ? raw : 0;
      tileCells[vertex * 2] = Math.round((CELL_UV[tile * 2] ?? 0) * 255);
      tileCells[vertex * 2 + 1] = Math.round((CELL_UV[tile * 2 + 1] ?? 0) * 255);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(data.normals, 3));
    geometry.setAttribute("uv", new THREE.BufferAttribute(data.uvs, 2));
    geometry.setAttribute("color", new THREE.BufferAttribute(data.colors, colorComponents));
    geometry.setAttribute(TILE_ATTRIBUTE, new THREE.BufferAttribute(tileCells, 2, true));
    geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
    // Recompute bounds before the mesh is added: a stale volume silently culls geometry that an
    // authoritative edit just added (convention 6).
    geometry.computeBoundingSphere();
    return geometry;
  }
}
